import React, { useState } from 'react';

import {
  Add as AddIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ArrowDownward as ArrowDownIcon2,
  ArrowUpward as ArrowUpIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  InputBase,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CustomModal from 'components/CustomModal';
import Loader from 'components/elements/Loader';
import useNavigateWithUUID from 'hooks/useNavigateWithUUID';

import { Contract, getStatusColor } from '../contractManagement.utils';

interface ContractsTableProps {
  contracts: Contract[];
  onViewContract: (contract: Contract) => void;
  loading?: boolean;
  balance: string;
  currency: string | null;
  onCreateNewContract: () => void;
}

type SortField = 'createdDate' | null;
type SortDirection = 'asc' | 'desc';

const ContractsTable: React.FC<ContractsTableProps> = ({
  contracts,
  onViewContract,
  loading = false,
  balance,
  currency,
  onCreateNewContract,
}) => {
  const { navigateWithUuid } = useNavigateWithUUID();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [showLowBalanceModal, setShowLowBalanceModal] = useState(false);
  const [sortField, setSortField] = useState<SortField>('createdDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const contractsPerPage = 8;

  // Format UTC timestamp to human-readable date
  const formatCreatedDate = (utcTimestamp: string): string => {
    try {
      const date = new Date(utcTimestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Handle sorting
  const handleSort = (field: SortField) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Sort contracts based on current sort field and direction
  const sortContracts = (contractsToSort: Contract[]): Contract[] => {
    return [...contractsToSort].sort((a, b) => {
      const aValue = new Date(a.contract_start_date_utc).getTime();
      const bValue = new Date(b.contract_start_date_utc).getTime();

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  // Get sort icon for a column
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowDownIcon2 sx={{ fontSize: 16, opacity: 0.3 }} />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUpIcon sx={{ fontSize: 16 }} />
    ) : (
      <ArrowDownIcon2 sx={{ fontSize: 16 }} />
    );
  };

  const handleCreateNewContract = () => {
    // const numericBalance = parseFloat(balance);
    // if (isNaN(numericBalance) || numericBalance < 2) {
    //   setShowLowBalanceModal(true);
    //   return;
    // }
    onCreateNewContract();
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filter changes
    handleFilterClose();
  };

  // Filter contracts based on search term and status filter
  const filteredContracts = contracts.filter((contract) => {
    const matchesSearch = (contract.contract_details.contractName || 'N/A')
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === 'All' || contract.contract_status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Sort the filtered contracts
  const sortedContracts = sortContracts(filteredContracts);

  // Calculate pagination
  const indexOfLastContract = currentPage * contractsPerPage;
  const indexOfFirstContract = indexOfLastContract - contractsPerPage;
  const currentContracts = sortedContracts.slice(
    indexOfFirstContract,
    indexOfLastContract
  );
  const totalPages = Math.ceil(sortedContracts.length / contractsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', spaceY: 3 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          }}
        >
          <Box sx={{ p: 3, pb: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                background: 'linear-gradient(135deg, #1270dc 0%, #42d392 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Your Contracts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track all your contract agreements
            </Typography>
          </Box>
          <Box sx={{ p: 3, pt: 0 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                    <TableCell sx={{ fontWeight: 600, py: 2, px: 3 }}>
                      Contract Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2, px: 3 }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2, px: 3 }}>
                      Created Date (UTC Time)
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, py: 2, px: 3, textAlign: 'right' }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          py: 6,
                        }}
                      >
                        <Loader size={40} />
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (contracts.length === 0) {
    return (
      <Box sx={{ width: '100%', spaceY: 3 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          }}
        >
          <Box sx={{ p: 3, pb: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                background: 'linear-gradient(135deg, #1270dc 0%, #42d392 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Your Contracts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track all your contract agreements
            </Typography>
          </Box>
          <Box sx={{ p: 3, pt: 0 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                    <TableCell sx={{ fontWeight: 600, py: 2, px: 3 }}>
                      Contract Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2, px: 3 }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2, px: 3 }}>
                      Created Date (UTC Time)
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, py: 2, px: 3, textAlign: 'right' }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          py: 6,
                          textAlign: 'center',
                        }}
                      >
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          sx={{ mb: 2, fontWeight: 600 }}
                        >
                          No Contracts Found
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 3, maxWidth: '400px' }}
                        >
                          You haven't created any contracts yet. Start by
                          creating your first contract to manage your documents
                          and agreements.
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleCreateNewContract}
                          sx={{
                            background: '#3b82f6',
                            color: 'white',
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            '&:hover': {
                              background: '#2563eb',
                            },
                          }}
                        >
                          New Contract
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', spaceY: 3 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  background:
                    'linear-gradient(135deg, #1270dc 0%, #42d392 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Your Contracts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and track all your contract agreements
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNewContract}
              sx={{
                background: '#3b82f6',
                color: 'white',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  background: '#2563eb',
                },
              }}
            >
              New Contract
            </Button>
          </Box>

          {/* Search and Filter */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Box sx={{ position: 'relative', flex: 1 }}>
              <SearchIcon
                sx={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'text.secondary',
                  fontSize: 20,
                }}
              />
              <InputBase
                placeholder="Search contracts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  pl: 5,
                  pr: 2,
                  py: 1,
                  width: '100%',
                  bgcolor: 'background.paper',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  fontSize: '0.875rem',
                  '&:focus': {
                    borderColor: '#3b82f6',
                    boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.1)',
                  },
                }}
              />
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterIcon />}
              onClick={handleFilterClick}
              endIcon={<ArrowDownIcon />}
              sx={{
                borderColor: '#e2e8f0',
                color: 'text.primary',
                textTransform: 'none',
                px: 2,
                py: 1,
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#3b82f6',
                  bgcolor: 'rgba(59, 130, 246, 0.05)',
                },
              }}
            >
              {statusFilter === 'All' ? 'Filter by Status' : statusFilter}
            </Button>
          </Box>
        </Box>

        {/* Table */}
        <Box sx={{ p: 0, width: '100%' }}>
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
              bgcolor: 'background.paper',
              width: '100%',
            }}
          >
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <TableCell sx={{ fontWeight: 600, py: 3, px: 3 }}>
                    Contract Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 3, px: 3 }}>
                    Status
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, py: 3, px: 3, cursor: 'pointer' }}
                    onClick={() => handleSort('createdDate')}
                  >
                    Created Date (UTC Time)
                    {getSortIcon('createdDate')}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, py: 3, px: 3, textAlign: 'right' }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentContracts.map((contract, index) => (
                  <TableRow
                    key={contract.contract_id}
                    sx={{
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                      transition: 'background-color 0.2s ease',
                      borderBottom: '1px solid #e2e8f0',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <TableCell sx={{ py: 3, px: 3 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {contract.contract_details.contractName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 3, px: 3 }}>
                      <Chip
                        label={contract.contract_status}
                        color={getStatusColor(contract.contract_status)}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          px: 2,
                          py: 0.5,
                          borderRadius: '16px',
                          border: 'none',
                          bgcolor:
                            contract.contract_status === 'In Progress'
                              ? '#ff9800'
                              : contract.contract_status === 'Initiated'
                                ? '#f5f5f5'
                                : contract.contract_status === 'Complete'
                                  ? '#e8f5e8'
                                  : '#f44336',
                          color:
                            contract.contract_status === 'In Progress'
                              ? '#ffffff'
                              : contract.contract_status === 'Complete'
                                ? '#2e7d32'
                                : '#333',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 3, px: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatCreatedDate(contract.contract_start_date_utc)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 3, px: 3, textAlign: 'right' }}>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<ViewIcon sx={{ fontSize: 16 }} />}
                        onClick={() => onViewContract(contract)}
                        sx={{
                          color: '#3b82f6',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          px: 1,
                          py: 0.5,
                          textTransform: 'none',
                          opacity: 0.7,
                          '&:hover': {
                            // bgcolor: '#3b82f6',
                            // color: '#ffffff',
                            opacity: 1,
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Paper>

      {/* Pagination */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          fontSize: '0.875rem',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">
          Showing {sortedContracts.length} contracts
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mt: 5, alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            sx={{
              borderColor: '#e2e8f0',
              color: 'text.primary',
              bgcolor: 'background.paper',
              borderRadius: 1,
              px: 2,
              py: 0.5,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#3b82f6',
                bgcolor: 'rgba(59, 130, 246, 0.05)',
              },
              '&:disabled': {
                borderColor: '#e2e8f0',
                color: 'text.disabled',
              },
            }}
          >
            Previous
          </Button>

          <Button
            variant="contained"
            size="small"
            sx={{
              bgcolor: '#3b82f6',
              color: '#ffffff',
              borderRadius: 2,
              px: 2,
              py: 0.5,
              fontSize: '0.875rem',
              textTransform: 'none',
              minWidth: '32px',
              '&:hover': {
                bgcolor: '#2563eb',
              },
            }}
          >
            {currentPage}
          </Button>

          <Button
            variant="outlined"
            size="small"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            sx={{
              borderColor: '#e2e8f0',
              color: 'text.primary',
              bgcolor: 'background.paper',
              borderRadius: 1,
              px: 2,
              py: 0.5,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#3b82f6',
                bgcolor: 'rgba(59, 130, 246, 0.05)',
              },
              '&:disabled': {
                borderColor: '#e2e8f0',
                color: 'text.disabled',
              },
            }}
          >
            Next
          </Button>
        </Box>
      </Box>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{ mt: 1 }}
      >
        <MenuItem
          onClick={() => handleStatusFilterChange('All')}
          selected={statusFilter === 'All'}
        >
          All
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusFilterChange('Initiated')}
          selected={statusFilter === 'Initiated'}
        >
          Initiated
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusFilterChange('In Progress')}
          selected={statusFilter === 'In Progress'}
        >
          In Progress
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusFilterChange('Complete')}
          selected={statusFilter === 'Complete'}
        >
          Complete
        </MenuItem>
      </Menu>

      {/* Modal for low balance */}
      <CustomModal
        open={showLowBalanceModal}
        onClose={() => setShowLowBalanceModal(false)}
        type="warning"
        title="Insufficient Balance"
        primaryButtonLabel="OK"
        primaryButtonAction={() => setShowLowBalanceModal(false)}
      >
        <Typography variant="body1">
          You need a minimum balance of 2 {currency || 'USD'} to create a new
          contract. Your current balance is {balance}.
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          Please add funds to your account or contact your administrator to
          update your balance before creating a new contract.
        </Typography>
      </CustomModal>
    </Box>
  );
};

export default ContractsTable;
